// src/services/eventUpgradeInvoiceTemplates.js
// HTML template generation for event tier upgrade invoices

import { PLATFORM_DETAILS } from './eventUpgradeInvoiceService';
import { amountInWords as wordsFor, invoiceCurrency, moneyOnInvoice, placeOfSupply, supplyNote, isExportInvoice } from './invoiceMoney';

/**
 * Get tier type display name
 */
const getTierTypeLabel = (tierType) => {
  switch (tierType) {
    case 'host':
      return 'Host Features';
    case 'photo':
      return 'Photo Upload';
    default:
      return 'Event Upgrade';
  }
};

/**
 * Get tier service description
 */
const getServiceDescription = (tierType, tierDisplayName) => {
  switch (tierType) {
    case 'host':
      return `Event Host Upgrade - ${tierDisplayName} Plan`;
    case 'photo':
      return `Photo Upload Upgrade - ${tierDisplayName} Plan`;
    default:
      return `Event Upgrade - ${tierDisplayName} Plan`;
  }
};

/**
 * Format date for invoice display
 */
const formatInvoiceDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Generate Event Tier Upgrade Invoice HTML
 * @param {Object} invoice - Invoice data from database
 * @returns {string} - HTML string for invoice
 */
export const generateEventTierInvoiceHTML = (invoice) => {
  const {
    invoice_number,
    tier_type,
    tier_name,
    tier_display_name,
    event_title,
    base_amount,
    gst_type,
    gst_rate = 18,
    cgst,
    sgst,
    igst,
    total_amount,
    customer_name,
    customer_email,
    customer_phone,
    customer_address,
    customer_city,
    generated_at,
    hsn_sac_code = '998314',
    seller_name = PLATFORM_DETAILS.name,
    seller_trade_name = PLATFORM_DETAILS.tradeName,
    seller_gstin = PLATFORM_DETAILS.gstin,
    seller_pan = PLATFORM_DETAILS.pan,
    seller_address = PLATFORM_DETAILS.address,
    seller_state = PLATFORM_DETAILS.state,
    seller_state_code = PLATFORM_DETAILS.stateCode,
    seller_email = PLATFORM_DETAILS.email,
  } = invoice;

  const invoiceDate = formatInvoiceDate(generated_at);
  const currency = invoiceCurrency(invoice);
  const money = (v) => moneyOnInvoice(v, currency);
  const serviceDescription = getServiceDescription(tier_type, tier_display_name || tier_name);
  const tierTypeLabel = getTierTypeLabel(tier_type);

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=794, initial-scale=1.0">
  <title>Event Upgrade Invoice - ${invoice_number}</title>
  <style>
    /* A4 at 96 DPI: 794px x 1123px */
    @page {
      size: A4;
      margin: 0;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 794px;
      height: 1123px;
      margin: 0;
      padding: 0;
      background: white;
      overflow: hidden;
    }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      line-height: 1.4;
      color: #333;
      font-size: 11px;
    }
    .invoice-container {
      width: 794px;
      height: 1123px;
      margin: 0;
      padding: 50px 55px 40px 55px;
      background: white;
      overflow: hidden;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #7C4DFF;
      padding-bottom: 8px;
      margin-bottom: 16px;
    }
    .header h1 {
      color: #7C4DFF;
      font-size: 22px;
      margin-bottom: 2px;
      font-weight: 700;
    }
    .header .trade-name {
      font-size: 10px;
      color: #666;
      margin-bottom: 4px;
    }
    .header .doc-type {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-top: 5px;
    }
    .header .service-type {
      font-size: 9px;
      color: #888;
      margin-top: 2px;
    }
    .invoice-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
      padding: 8px 10px;
      background: #f8f9fa;
      border-radius: 5px;
      font-size: 10px;
    }
    .invoice-info div {
      flex: 1;
    }
    .invoice-info .right {
      text-align: right;
    }
    .invoice-info strong {
      color: #555;
    }
    .event-banner {
      background: linear-gradient(135deg, #7C4DFF22 0%, #6B3FE422 100%);
      border: 1px solid #7C4DFF44;
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 12px;
    }
    .event-banner .label {
      font-size: 8px;
      color: #7C4DFF;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }
    .event-banner .event-title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
    .section-title {
      background: linear-gradient(135deg, #7C4DFF 0%, #6B3FE4 100%);
      color: white;
      padding: 5px 8px;
      font-weight: 600;
      margin: 0 0 8px 0;
      border-radius: 4px;
      font-size: 10px;
    }
    .details-grid {
      display: flex;
      gap: 12px;
      margin-bottom: 10px;
    }
    .details-box {
      flex: 1;
      padding: 8px;
      background: #fafafa;
      border-radius: 5px;
      border: 1px solid #eee;
    }
    .details-box .label {
      font-weight: 600;
      color: #666;
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      margin-bottom: 1px;
    }
    .details-box .value {
      color: #333;
      font-size: 10px;
      margin-bottom: 5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    th {
      background: #7C4DFF;
      color: white;
      padding: 6px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 9px;
    }
    th:last-child {
      text-align: right;
    }
    td {
      padding: 6px 8px;
      border-bottom: 1px solid #eee;
      font-size: 10px;
    }
    .text-right {
      text-align: right;
    }
    .gst-row td {
      background: #f9f9f9;
      font-size: 9px;
      padding: 5px 8px;
    }
    .total-row {
      background: linear-gradient(135deg, #7C4DFF 0%, #6B3FE4 100%);
    }
    .total-row td {
      color: white;
      font-weight: 700;
      font-size: 11px;
      border: none;
    }
    .amount-words {
      background: #f0f0f0;
      padding: 8px 10px;
      margin: 10px 0;
      border-left: 3px solid #7C4DFF;
      border-radius: 0 5px 5px 0;
      font-size: 10px;
    }
    .amount-words strong {
      color: #7C4DFF;
    }
    .tier-badge {
      display: inline-block;
      background: #7C4DFF;
      color: white;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 8px;
      font-weight: 600;
      margin-left: 6px;
    }
    .footer {
      margin-top: 15px;
      padding-top: 10px;
      border-top: 1px solid #eee;
      font-size: 9px;
      color: #666;
    }
    .footer p {
      margin-bottom: 2px;
    }
    .hsn-note {
      font-size: 8px;
      color: #888;
      margin-top: 2px;
    }
    @media print {
      @page {
        size: A4;
        margin: 0;
      }
      html, body {
        width: 210mm;
        height: 297mm;
        margin: 0;
        padding: 0;
      }
      .invoice-container {
        width: 210mm;
        height: 297mm;
        padding: 15mm 16mm 12mm 16mm;
        border: none;
        box-shadow: none;
        page-break-after: avoid;
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <h1>${seller_name}</h1>
      <div class="trade-name">(A product of ${seller_trade_name})</div>
      <div class="doc-type">TAX INVOICE</div>
      <div class="service-type">Event Upgrade Services</div>
    </div>

    <div class="invoice-info">
      <div>
        <strong>Invoice No:</strong> ${invoice_number}<br>
        <strong>Invoice Date:</strong> ${invoiceDate}
      </div>
      <div class="right">
        <strong>GSTIN:</strong> ${seller_gstin}<br>
        <strong>State:</strong> ${seller_state} (${seller_state_code})
      </div>
    </div>

    <div class="event-banner">
      <div class="label">Event</div>
      <div class="event-title">${event_title || 'N/A'}</div>
    </div>

    <div class="details-grid">
      <div class="details-box">
        <div class="section-title" style="margin: 0 0 15px 0; padding: 8px 12px;">Seller Details</div>
        <div class="label">Platform</div>
        <div class="value">${seller_name}</div>
        <div class="label">Trade Name</div>
        <div class="value">${seller_trade_name}</div>
        <div class="label">Address</div>
        <div class="value">${seller_address}</div>
        <div class="label">GSTIN</div>
        <div class="value">${seller_gstin}</div>
        <div class="label">PAN</div>
        <div class="value">${seller_pan}</div>
      </div>

      <div class="details-box">
        <div class="section-title" style="margin: 0 0 15px 0; padding: 8px 12px;">Buyer Details</div>
        <div class="label">Name</div>
        <div class="value">${customer_name || 'N/A'}</div>
        ${customer_email ? `<div class="label">Email</div><div class="value">${customer_email}</div>` : ''}
        ${customer_phone ? `<div class="label">Phone</div><div class="value">${customer_phone}</div>` : ''}
        ${customer_address ? `<div class="label">Address</div><div class="value">${customer_address}</div>` : ''}
        ${customer_city ? `<div class="label">City</div><div class="value">${customer_city}</div>` : ''}
        <div class="label">${isExportInvoice(invoice) ? 'Place of Supply' : 'State (Place of Supply)'}</div>
        <div class="value">${placeOfSupply(invoice)}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>HSN/SAC</th>
          <th class="text-right">Upgrade Type</th>
          <th class="text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            ${serviceDescription}
            <span class="tier-badge">${tierTypeLabel}</span>
            <div class="hsn-note">Digital Content & Online Services</div>
          </td>
          <td>${hsn_sac_code}</td>
          <td class="text-right">${tier_display_name || tier_name}</td>
          <td class="text-right">${money(base_amount)}</td>
        </tr>
        ${gst_type === 'intrastate' ? `
        <tr class="gst-row">
          <td colspan="3" class="text-right">CGST @ ${gst_rate / 2}%</td>
          <td class="text-right">${money(cgst)}</td>
        </tr>
        <tr class="gst-row">
          <td colspan="3" class="text-right">SGST @ ${gst_rate / 2}%</td>
          <td class="text-right">${money(sgst)}</td>
        </tr>
        ` : `
        <tr class="gst-row">
          <td colspan="3" class="text-right">IGST @ ${gst_rate}%</td>
          <td class="text-right">${money(igst)}</td>
        </tr>
        `}
        <tr class="total-row">
          <td colspan="3" class="text-right"><strong>Total Amount</strong></td>
          <td class="text-right"><strong>${money(total_amount)}</strong></td>
        </tr>
      </tbody>
    </table>

    <div class="amount-words">
      <strong>Amount in Words:</strong> ${wordsFor(total_amount, currency)} Only
      <div>${supplyNote(invoice)}</div>
    </div>

    <div class="footer">
      <p><strong>Terms & Conditions:</strong></p>
      <p>1. This is a computer-generated tax invoice and does not require a signature.</p>
      <p>2. Upgrade services are subject to our Terms of Service.</p>
      <p>3. For any queries, contact ${seller_email}</p>
      <p>4. HSN/SAC Code 998314: Online content services.</p>
      <p>5. Upgrades are non-refundable and apply to the specific event only.</p>
    </div>
  </div>
</body>
</html>
  `;

  return html;
};

/**
 * Open invoice in new window for printing
 */
export const openInvoiceForPrint = (invoiceHtml, invoiceNumber) => {
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (printWindow) {
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    printWindow.document.title = `Invoice - ${invoiceNumber}`;
    // Auto-trigger print dialog after content loads
    printWindow.onload = () => {
      printWindow.focus();
      // Small delay to ensure styles are applied
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }
  return printWindow;
};

/**
 * Download invoice as HTML file
 */
export const downloadInvoiceAsHtml = (invoiceHtml, invoiceNumber) => {
  const blob = new Blob([invoiceHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Invoice-${invoiceNumber}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate invoice filename
 */
export const getInvoiceFilename = (invoiceNumber, extension = 'html') => {
  return `Invoice-${invoiceNumber}.${extension}`;
};

const eventUpgradeInvoiceTemplatesExports = {
  generateEventTierInvoiceHTML,
  openInvoiceForPrint,
  downloadInvoiceAsHtml,
  getInvoiceFilename,
};

export default eventUpgradeInvoiceTemplatesExports;
